/**
 * Usage Pinia Store - TalkCel
 * Tracks chatbot and conversation usage.
 */
import { defineStore } from 'pinia'
import { ref, computed, readonly, watch } from 'vue'
import { useUserStore } from '~/stores/user'
import { useCompanyStore } from '~/stores/company'

export const useUsageStore = defineStore('usage', () => {
  const chatbotUsage = ref({
    currentChatbots: 0, chatbotLimit: 0, chatbotsRemaining: 0,
    usagePercentage: 0, isNearLimit: false, isAtLimit: false
  })

  const conversationUsage = ref({
    currentUsage: 0, monthlyLimit: 0, remaining: 0,
    usagePercentage: 0, isNearLimit: false, isAtLimit: false
  })

  const planLimits = ref({
    maxChatbots: 0, maxConversations: 0, planTier: '', planName: ''
  })

  const hasInteractionPoints = ref(false)
  const hasWebsiteScanning = ref(false)
  const hasTeamMembers = ref(false)

  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchTime = ref(0)
  const CACHE_TTL = 30 * 1000
  const isPollingActive = ref(false)
  const pollingInterval = ref<ReturnType<typeof setInterval> | null>(null)
  const POLLING_INTERVAL = 60 * 1000

  const isDataStale = computed(() => Date.now() - lastFetchTime.value > CACHE_TTL)

  const fetchUsageData = async (force = false) => {
    if (!force && !isDataStale.value && lastFetchTime.value > 0) return
    if (isLoading.value) {
      while (isLoading.value) await new Promise(resolve => setTimeout(resolve, 100))
      return
    }

    try {
      isLoading.value = true
      error.value = null

      // Keep whatever we already show on failure — substituting free-tier
      // numbers here made a transient 500 look like a real downgrade.
      const response = await $fetch<any>('/api/usage/current').catch(() => null)
      if (!response) {
        error.value = 'Failed to fetch usage data'
        return
      }

      const rawChatbotLimit = response.chatbots?.limit ?? 1
      const chatbotLimit = rawChatbotLimit === -1 ? Infinity : rawChatbotLimit
      const chatbotCurrent = response.chatbots?.current || 0
      const chatbotPercentage = Number.isFinite(chatbotLimit) && chatbotLimit > 0
        ? (chatbotCurrent / chatbotLimit) * 100
        : 0

      chatbotUsage.value = {
        currentChatbots: chatbotCurrent,
        chatbotLimit: Number.isFinite(chatbotLimit) ? chatbotLimit : -1,
        chatbotsRemaining: Number.isFinite(chatbotLimit) ? Math.max(0, chatbotLimit - chatbotCurrent) : Infinity,
        usagePercentage: Math.round(chatbotPercentage * 10) / 10,
        isNearLimit: chatbotPercentage >= 80 && chatbotPercentage < 100,
        isAtLimit: chatbotPercentage >= 100
      }

      const rawConvLimit = response.conversations?.limit ?? 100
      const convLimit = rawConvLimit === -1 ? Infinity : rawConvLimit
      const convCurrent = response.conversations?.current || 0
      const convPercentage = Number.isFinite(convLimit) && convLimit > 0
        ? (convCurrent / convLimit) * 100
        : 0

      conversationUsage.value = {
        currentUsage: convCurrent,
        monthlyLimit: Number.isFinite(convLimit) ? convLimit : -1,
        remaining: Number.isFinite(convLimit) ? Math.max(0, convLimit - convCurrent) : Infinity,
        usagePercentage: Math.round(convPercentage * 10) / 10,
        isNearLimit: convPercentage >= 80 && convPercentage < 100,
        isAtLimit: convPercentage >= 100
      }

      const plan = response.plan
      if (plan) {
        planLimits.value = {
          maxChatbots: Number.isFinite(chatbotLimit) ? chatbotLimit : -1,
          maxConversations: Number.isFinite(convLimit) ? convLimit : -1,
          planTier: plan.tier,
          planName: plan.name
        }
        hasInteractionPoints.value = !!plan.interactionPoints
        hasWebsiteScanning.value = !!plan.websiteScanning
        hasTeamMembers.value = !!plan.teamMembers
      }

      lastFetchTime.value = Date.now()
    } catch (err: any) {
      console.error('Failed to fetch usage data:', err)
      error.value = err.message || 'Failed to fetch usage data'
    } finally {
      isLoading.value = false
    }
  }

  const clearUsageData = () => {
    chatbotUsage.value = { currentChatbots: 0, chatbotLimit: 0, chatbotsRemaining: 0, usagePercentage: 0, isNearLimit: false, isAtLimit: false }
    conversationUsage.value = { currentUsage: 0, monthlyLimit: 0, remaining: 0, usagePercentage: 0, isNearLimit: false, isAtLimit: false }
    hasInteractionPoints.value = false; hasWebsiteScanning.value = false; hasTeamMembers.value = false
    lastFetchTime.value = 0; error.value = null
  }

  const startPolling = () => {
    if (process.server || isPollingActive.value) return
    isPollingActive.value = true
    fetchUsageData(false)
    pollingInterval.value = setInterval(() => { if (process.client) fetchUsageData(false) }, POLLING_INTERVAL)
  }

  const stopPolling = () => {
    if (!isPollingActive.value) return
    isPollingActive.value = false
    if (pollingInterval.value) { clearInterval(pollingInterval.value); pollingInterval.value = null }
  }

  const invalidateCache = () => { lastFetchTime.value = 0 }
  const refreshUsage = async () => fetchUsageData(true)

  return {
    chatbotUsage: readonly(chatbotUsage), conversationUsage: readonly(conversationUsage),
    planLimits: readonly(planLimits),
    hasInteractionPoints: readonly(hasInteractionPoints), hasWebsiteScanning: readonly(hasWebsiteScanning),
    hasTeamMembers: readonly(hasTeamMembers),
    isLoading: readonly(isLoading), error: readonly(error), isPollingActive: readonly(isPollingActive),
    isDataStale: readonly(isDataStale),
    fetchUsageData, clearUsageData, invalidateCache, startPolling, stopPolling, refreshUsage
  }
})

export const useUsageState = () => {
  const usageStore = useUsageStore()
  const userStore = useUserStore()
  const companyStore = useCompanyStore()

  if (process.client) {
    watch(
      () => [userStore.isAuthenticated, companyStore.hasCurrentCompany],
      ([isAuthenticated, hasCompany]) => {
        if (isAuthenticated && hasCompany && usageStore.isDataStale) usageStore.fetchUsageData()
        else if (!isAuthenticated) usageStore.clearUsageData()
      },
      { immediate: true }
    )
  }

  const canCreateChatbot = computed(() => !usageStore.chatbotUsage.isAtLimit)
  const canSendMessage = computed(() => !usageStore.conversationUsage.isAtLimit)

  const getUpgradeMessage = () => {
    if (usageStore.chatbotUsage.isAtLimit) return `You've reached your chatbot limit of ${usageStore.chatbotUsage.chatbotLimit}. Upgrade your plan to create more chatbots.`
    if (usageStore.conversationUsage.isAtLimit) return `You've reached your conversation limit of ${usageStore.conversationUsage.monthlyLimit} for this month. Upgrade for more conversations.`
    return null
  }

  return {
    chatbotUsage: usageStore.chatbotUsage, conversationUsage: usageStore.conversationUsage,
    planLimits: usageStore.planLimits,
    hasInteractionPoints: usageStore.hasInteractionPoints, hasWebsiteScanning: usageStore.hasWebsiteScanning,
    hasTeamMembers: usageStore.hasTeamMembers,
    isLoading: usageStore.isLoading, error: usageStore.error,
    canCreateChatbot: readonly(canCreateChatbot), canSendMessage: readonly(canSendMessage),
    fetchUsageData: usageStore.fetchUsageData, invalidateCache: usageStore.invalidateCache,
    getUpgradeMessage
  }
}
